import {
  AskStrikeUpButton,
  Box,
  Button,
  Card,
  CollapsibleSection,
  Flex,
  Heading,
  HStack,
  Input,
  PreHeader,
  RadioCardGroup,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  ToggleRow,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import { MdEmail, MdSave } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import WidgetSelector from "views/admin/widgets/components/WidgetSelector";
import { useWidgetEditor } from "views/admin/widgets/hooks/useWidgetEditor";

const AiCapabilitiesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | undefined>(
    () => {
      const params = new URLSearchParams(window.location.search);
      return params.get("widgetId") ?? undefined;
    },
  );

  const handleSelect = (id: string) => {
    setSelectedWidgetId(id || undefined);
    const params = new URLSearchParams(window.location.search);
    if (id) {
      params.set("widgetId", id);
    } else {
      params.delete("widgetId");
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const {
    loading,
    saving,
    error,
    success,
    formData,
    setFormData,
    sharedDraft,
    setSharedDraft,
    agentsSaving,
    allSkills,
    widgetSkillIds,
    skillToggling,
    hasHandoffFeature,
    handoffEnabled,
    setHandoffEnabled,
    handoffLevel,
    setHandoffLevel,
    handoffIntegrationId,
    handoffInboxId,
    setHandoffInboxId,
    handoffInboxName,
    setHandoffInboxName,
    handoffMailboxRequired,
    integrations,
    inboxes,
    handoffSaving,
    handoffError,
    inboxesLoading,
    handleAiAgentsSave,
    handleSkillToggle,
    handleIntegrationChange,
    handleHandoffSave,
    allNotificationChannels,
    notificationEnabled,
    setNotificationEnabled,
    notificationChannelLinks,
    setNotificationChannelLinks,
    notificationSaving,
    notificationError,
    handleNotificationSave,
  } = useWidgetEditor(selectedWidgetId);

  const [aiOpen, setAiOpen] = useState(true);
  const [skillsOpen, setSkillsOpen] = useState(true);
  const [handoffOpen, setHandoffOpen] = useState(true);
  const [notificationOpen, setNotificationOpen] = useState(true);
  const [selectorInView, setSelectorInView] = useState(true);
  const [bannerLeft, setBannerLeft] = useState(0);
  const selectorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset collapsibles when widget changes
  useEffect(() => {
    setAiOpen(true);
    setSkillsOpen(true);
    setHandoffOpen(true);
    setNotificationOpen(true);
  }, [selectedWidgetId]);

  // Track whether the widget selector is scrolled out of view using window scroll
  useEffect(() => {
    const check = () => {
      const el = selectorRef.current;
      if (!el) return;
      setSelectorInView(el.getBoundingClientRect().bottom > 0);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);

  // Track the left offset of the content area for the fixed banner
  useEffect(() => {
    const update = () => {
      if (contentRef.current) {
        setBannerLeft(contentRef.current.getBoundingClientRect().left);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <Stack gap={5} w="full" ref={contentRef}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={2}>
        <VStack align="start" gap={1}>
          <Heading size="lg" color="var(--text-primary)">
            AI & UX
          </Heading>
          <Text fontSize="sm" color="var(--text-secondary-color)" m={0}>
            Configure agents, skills, handoff, and email notifications per
            widget.
          </Text>
        </VStack>
        <AskStrikeUpButton
          context={t("app.askStrikeUp.context.aiCapabilities")}
        />
      </Flex>

      {/* Widget selector */}
      <Box ref={selectorRef}>
        <WidgetSelector
          selectedWidgetId={selectedWidgetId}
          onSelect={handleSelect}
        />
      </Box>

      {/* Fixed context banner — shown only when widget selector is scrolled out of view */}
      {selectedWidgetId && !loading && formData.name && !selectorInView && (
        <HStack
          position="fixed"
          top={0}
          left={`${bannerLeft}px`}
          right={0}
          zIndex={49}
          px={4}
          py={2}
          bg={{ base: "blue.50", _dark: "blue.950" }}
          borderBottomWidth="1px"
          borderColor={{ base: "blue.200", _dark: "blue.700" }}
          gap={2}
          boxShadow="sm"
        >
          <Text fontSize="xs" color={{ base: "blue.600", _dark: "blue.300" }}>
            Editing settings for
          </Text>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={{ base: "blue.700", _dark: "blue.200" }}
          >
            {formData.name}
          </Text>
        </HStack>
      )}

      {!selectedWidgetId ? (
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color="var(--text-secondary-color)"
              py={6}
              m={0}
            >
              Select a widget above to configure its AI settings.
            </Text>
          </Card.Body>
        </Card.Root>
      ) : loading ? (
        <Card.Root>
          <Card.Body>
            <Text textAlign="center" color="var(--text-primary)" m={0}>
              Loading…
            </Text>
          </Card.Body>
        </Card.Root>
      ) : (
        <>
          {error && (
            <Box
              p={3}
              borderRadius="10px"
              bg="rgba(239, 68, 68, 0.08)"
              borderWidth="1px"
              borderColor="var(--accent-danger, #EF4444)"
            >
              <Text color="var(--text-primary)" fontSize="sm" m={0}>
                {error}
              </Text>
            </Box>
          )}
          {success && (
            <Box
              p={3}
              borderRadius="10px"
              bg="rgba(34, 197, 94, 0.08)"
              borderWidth="1px"
              borderColor="var(--accent-success, #22C55E)"
            >
              <Text color="var(--text-primary)" fontSize="sm" m={0}>
                {success}
              </Text>
            </Box>
          )}

          {/* Persona */}
          <CollapsibleSection
            pre="Persona"
            title="Persona"
            description="Tone, behavior and branding instructions applied to all agents"
            isOpen={aiOpen}
            onOpenChange={setAiOpen}
          >
            <Stack gap={4}>
              {/* AI provider / model */}
              {/* <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <Select
                  label="AI Provider"
                  items={[{ label: 'OpenAI', value: 'OpenAI' }]}
                  value={[formData.aiProvider]}
                  onValueChange={(e: any) => {
                    const provider = e.value[0];
                    setFormData({ ...formData, aiProvider: provider, aiModel: provider === 'OpenAI' ? 'gpt-5.2' : 'gemini-3' });
                  }}
                />
                <Select
                  label="AI Model"
                  items={formData.aiProvider === 'OpenAI' ? [
                    { label: 'GPT-4o', value: 'gpt-4o' },
                    { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
                    { label: 'GPT-5.1', value: 'gpt-5.1' },
                    { label: 'GPT-5 Mini', value: 'gpt-5-mini' },
                    { label: 'GPT-5.2', value: 'gpt-5.2' },
                  ] : [
                    { label: 'Gemini 2.5 Flash', value: 'gemini-2.5-flash' },
                    { label: 'Gemini 3 Pro', value: 'gemini-3-pro-preview' },
                  ]}
                  value={[formData.aiModel]}
                  onValueChange={(e: any) => setFormData({ ...formData, aiModel: e.value[0] })}
                />
              </Grid> */}

              <Input
                label="Tone of voice (optional)"
                value={sharedDraft.toneOfVoice}
                onChange={(e) =>
                  setSharedDraft((p) => ({ ...p, toneOfVoice: e.target.value }))
                }
                placeholder="e.g. professional and helpful"
              />
              <Textarea
                label="Behavior instructions (optional)"
                value={sharedDraft.behaviorInstructions}
                onChange={(e) =>
                  setSharedDraft((p) => ({
                    ...p,
                    behaviorInstructions: e.target.value,
                  }))
                }
                placeholder="Specific behavior guidelines for all agents"
                rows={3}
              />
              <Textarea
                label="Branding instructions (optional)"
                value={sharedDraft.brandingInstructions}
                onChange={(e) =>
                  setSharedDraft((p) => ({
                    ...p,
                    brandingInstructions: e.target.value,
                  }))
                }
                placeholder="Brand voice and identity instructions"
                rows={3}
              />

              <HStack>
                <Button
                  onClick={handleAiAgentsSave}
                  intent="primary"
                  disabled={agentsSaving || saving}
                  alignSelf="flex-start"
                >
                  <MdSave />
                  {agentsSaving || saving ? "Saving…" : "Save Persona"}
                </Button>
              </HStack>
            </Stack>
          </CollapsibleSection>

          {/* Skills */}
          {allSkills.length > 0 && (
            <CollapsibleSection
              pre="Skills"
              title="Skills"
              description="Enable skills available to all agents in this widget"
              isOpen={skillsOpen}
              onOpenChange={setSkillsOpen}
            >
              <Stack gap={2}>
                <HStack justify="flex-end" mb={1}>
                  <Text
                    fontFamily="var(--font-mono)"
                    fontSize="10px"
                    letterSpacing="0.10em"
                    textTransform="uppercase"
                    color="var(--brand-primary, #3B82F6)"
                    cursor="pointer"
                    onClick={() => navigate("/admin/skills")}
                    m={0}
                  >
                    Manage Skills →
                  </Text>
                </HStack>
                {allSkills.map((skill) => {
                  const assigned = widgetSkillIds.includes(skill.id);
                  const toggling = skillToggling === skill.id;
                  return (
                    <HStack
                      key={skill.id}
                      justify="space-between"
                      opacity={toggling ? 0.5 : 1}
                      p={3}
                      borderWidth="1px"
                      borderRadius="10px"
                      borderColor={
                        assigned
                          ? "var(--brand-primary, #3B82F6)"
                          : "var(--border-default)"
                      }
                      bg={
                        assigned
                          ? "rgba(59, 130, 246, 0.06)"
                          : "var(--bg-primary)"
                      }
                      transition="background-color 0.15s, border-color 0.15s"
                    >
                      <VStack align="start" gap={0} flex={1} minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight={500}
                          color="var(--text-primary)"
                          m={0}
                        >
                          {skill.name}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="var(--text-secondary-color)"
                          truncate
                          m={0}
                        >
                          {skill.triggerContext}
                        </Text>
                      </VStack>
                      <Switch
                        checked={assigned}
                        onCheckedChange={(e: any) =>
                          handleSkillToggle(skill.id, e.checked)
                        }
                        colorPalette="blue"
                        size="sm"
                        disabled={toggling}
                      />
                    </HStack>
                  );
                })}
              </Stack>
            </CollapsibleSection>
          )}

          {/* Human Handoff */}
          {hasHandoffFeature && (
            <CollapsibleSection
              pre="Handoff"
              title="Human Handoff"
              description="Escalate conversations to a live agent when the AI can't resolve the issue"
              isOpen={handoffOpen}
              onOpenChange={setHandoffOpen}
            >
              <Stack gap={5}>
                {/* Integration warning — shown at top so users know before configuring */}
                {integrations.length === 0 && (
                  <Box
                    p={3}
                    borderRadius="10px"
                    bg="rgba(245, 158, 11, 0.08)"
                    borderWidth="1px"
                    borderColor="var(--accent-warning, #F59E0B)"
                  >
                    <Text fontSize="sm" color="var(--text-primary)" m={0}>
                      No support integrations connected. Go to{" "}
                      <Text
                        as="span"
                        fontWeight={500}
                        textDecoration="underline"
                        cursor="pointer"
                        onClick={() =>
                          window.open(
                            "/admin/integrations",
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        Integrations
                      </Text>{" "}
                      to connect Help Scout.
                    </Text>
                  </Box>
                )}

                {handoffError && (
                  <Box
                    p={3}
                    borderRadius="10px"
                    bg="rgba(239, 68, 68, 0.08)"
                    borderWidth="1px"
                    borderColor="var(--accent-danger, #EF4444)"
                  >
                    <Text fontSize="sm" color="var(--text-primary)" m={0}>
                      {handoffError}
                    </Text>
                  </Box>
                )}

                <ToggleRow
                  label='Enable "Invite an agent" button'
                  hint="Shows a handoff button in the widget chat UI"
                  value={handoffEnabled}
                  onChange={setHandoffEnabled}
                />

                <Box>
                  <Box mb={2}>
                    <PreHeader size="xs">Handoff sensitivity</PreHeader>
                  </Box>
                  <RadioCardGroup
                    value={String(handoffLevel)}
                    onChange={(v) => setHandoffLevel(Number(v))}
                    options={[
                      {
                        id: "0",
                        label: "Easy",
                        sub: "First mention of wanting human support triggers handoff suggestion",
                      },
                      {
                        id: "1",
                        label: "Normal",
                        sub: "AI tries to help first; suggests handoff if user asks again or remains unsatisfied",
                      },
                      {
                        id: "2",
                        label: "Hard",
                        sub: "AI exhausts all options; suggests handoff only if nothing works or user insists",
                      },
                    ]}
                  />
                </Box>

                {integrations.length > 0 && (
                  <Stack gap={4}>
                    <Select
                      label="Integration"
                      items={[
                        { label: "— None —", value: "" },
                        ...integrations.map((i) => ({
                          label: `${i.name} (${i.providerType})`,
                          value: i.id,
                        })),
                      ]}
                      value={[handoffIntegrationId]}
                      onValueChange={(e: any) =>
                        handleIntegrationChange(e.value[0])
                      }
                    />
                    {handoffIntegrationId && (
                      <>
                        <Select
                          label={
                            handoffMailboxRequired
                              ? t("app.supportIntegrations.handoff.mailbox")
                              : "Inbox / Team (optional)"
                          }
                          items={
                            inboxesLoading
                              ? [{ label: "Loading…", value: "" }]
                              : [
                                  {
                                    label: handoffMailboxRequired
                                      ? t(
                                          "app.supportIntegrations.handoff.chooseMailbox",
                                        )
                                      : "— None (default / unassigned) —",
                                    value: "",
                                  },
                                  ...inboxes.map((i) => ({
                                    label: i.name,
                                    value: i.id,
                                  })),
                                ]
                          }
                          value={[handoffInboxId]}
                          onValueChange={(e: any) => {
                            const selected = inboxes.find(
                              (i) => i.id === e.value[0],
                            );
                            setHandoffInboxId(e.value[0]);
                            setHandoffInboxName(selected?.name ?? "");
                          }}
                        />
                        {!inboxesLoading &&
                          inboxes.length === 0 &&
                          (handoffMailboxRequired ? (
                            <Text
                              fontSize="xs"
                              color="var(--accent-danger, #EF4444)"
                            >
                              {t("app.supportIntegrations.handoff.noMailboxes")}
                            </Text>
                          ) : (
                            <Text
                              fontSize="xs"
                              color={{ base: "gray.500", _dark: "gray.400" }}
                            >
                              No teams/mailboxes found. Conversations will go to
                              the default inbox.
                            </Text>
                          ))}
                        {handoffMailboxRequired &&
                          handoffEnabled &&
                          !handoffInboxId &&
                          inboxes.length > 0 && (
                            <Text
                              fontSize="xs"
                              color="var(--text-secondary-color)"
                            >
                              {t(
                                "app.supportIntegrations.handoff.mailboxRequired",
                              )}
                            </Text>
                          )}
                      </>
                    )}
                  </Stack>
                )}

                <HStack>
                  <Button
                    onClick={handleHandoffSave}
                    intent="primary"
                    disabled={
                      handoffSaving ||
                      saving ||
                      (handoffEnabled &&
                        handoffMailboxRequired &&
                        !handoffInboxId)
                    }
                    alignSelf="flex-start"
                  >
                    <MdSave />
                    {handoffSaving ? "Saving…" : "Save Handoff Settings"}
                  </Button>
                </HStack>
              </Stack>
            </CollapsibleSection>
          )}

          {/* Email Notifications */}
          {selectedWidgetId && (
            <CollapsibleSection
              pre="Notifications"
              title="Email Notifications"
              description="Let the AI send reports and data exports to users via email"
              isOpen={notificationOpen}
              onOpenChange={setNotificationOpen}
            >
              <Stack gap={4}>
                <ToggleRow
                  label="Enable AI email notifications"
                  hint="When enabled, the AI can send emails when users ask for reports or data exports"
                  value={notificationEnabled}
                  onChange={setNotificationEnabled}
                />

                {/* Channel list */}
                {allNotificationChannels.length === 0 ? (
                  <Box
                    borderWidth="1px"
                    borderRadius="10px"
                    borderStyle="dashed"
                    p={3.5}
                    borderColor="var(--border-default)"
                  >
                    <Text
                      fontSize="sm"
                      color="var(--text-secondary-color)"
                      m={0}
                    >
                      No notification channels configured yet. Go to{" "}
                      <Text
                        as="span"
                        fontWeight={500}
                        color="var(--text-primary)"
                      >
                        Notifications
                      </Text>{" "}
                      in the sidebar to add an SMTP channel first.
                    </Text>
                  </Box>
                ) : (
                  <Stack gap={2}>
                    <Box>
                      <PreHeader size="xs">Connected channels</PreHeader>
                    </Box>
                    {allNotificationChannels.map((channel) => {
                      const linked = notificationChannelLinks.find(
                        (l) => l.notificationChannelId === channel.id,
                      );
                      const isLinked = !!linked;

                      const toggleLink = () => {
                        if (isLinked) {
                          setNotificationChannelLinks((prev) =>
                            prev.filter(
                              (l) => l.notificationChannelId !== channel.id,
                            ),
                          );
                        } else {
                          setNotificationChannelLinks((prev) => [
                            ...prev,
                            {
                              notificationChannelId: channel.id,
                              channelName: channel.name,
                              providerType: channel.providerType,
                              isDefault: prev.length === 0,
                              isActive: channel.isActive,
                            },
                          ]);
                        }
                      };

                      const toggleDefault = () => {
                        setNotificationChannelLinks((prev) =>
                          prev.map((l) => ({
                            ...l,
                            isDefault: l.notificationChannelId === channel.id,
                          })),
                        );
                      };

                      return (
                        <Flex
                          key={channel.id}
                          alignItems="center"
                          gap={3}
                          p={3}
                          borderWidth="1px"
                          borderRadius="10px"
                          borderColor={
                            isLinked
                              ? "var(--brand-primary, #3B82F6)"
                              : "var(--border-default)"
                          }
                          bg={
                            isLinked
                              ? "rgba(59, 130, 246, 0.06)"
                              : "var(--bg-primary)"
                          }
                          transition="background-color 0.15s, border-color 0.15s"
                        >
                          <MdEmail
                            size={18}
                            color="var(--text-secondary-color)"
                          />
                          <Box flex={1}>
                            <Text
                              fontSize="sm"
                              fontWeight={500}
                              color="var(--text-primary)"
                              m={0}
                            >
                              {channel.name}
                            </Text>
                            <Text
                              fontSize="xs"
                              color="var(--text-secondary-color)"
                              m={0}
                            >
                              {channel.smtpSettings?.fromEmail} ·{" "}
                              {channel.status}
                              {!channel.isActive && " · Disabled"}
                            </Text>
                          </Box>
                          {isLinked && (
                            <Button
                              size="xs"
                              intent={
                                linked.isDefault ? "primary" : "secondary"
                              }
                              onClick={toggleDefault}
                            >
                              {linked.isDefault ? "Default" : "Set default"}
                            </Button>
                          )}
                          <Switch
                            checked={isLinked}
                            onCheckedChange={toggleLink}
                          />
                        </Flex>
                      );
                    })}
                  </Stack>
                )}

                {notificationError && (
                  <Text
                    color="var(--accent-danger, #EF4444)"
                    fontSize="sm"
                    m={0}
                  >
                    {notificationError}
                  </Text>
                )}

                <HStack gap={2}>
                  <Button
                    intent="primary"
                    size="sm"
                    onClick={handleNotificationSave}
                    loading={notificationSaving}
                    alignSelf="flex-start"
                  >
                    <MdSave />
                    {notificationSaving
                      ? "Saving…"
                      : "Save Notification Settings"}
                  </Button>
                </HStack>
              </Stack>
            </CollapsibleSection>
          )}
        </>
      )}
    </Stack>
  );
};

export default AiCapabilitiesPage;
